import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Search, X } from 'lucide-react'
import type { components } from '@/api/schema'
import { useAuthenticatedClient } from '@/api/queries'
import { cn, formatNumber, parseUTCDate } from '@/lib/utils'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { SearchInput } from '@/components/search-input'

type Accuracy = components['schemas']['ShipmentAccuracyRead']
type Group = components['schemas']['ShipmentAccuracyGroup']
type Product = components['schemas']['ShipmentAccuracyProduct']
type View = 'manufacturer' | 'category' | 'product'
type Sort = 'gap' | 'shipped' | 'name'

// Drill-down from a manufacturer or category row into its products.
interface GroupFilter {
  kind: 'manufacturer' | 'category'
  id: number
  name: string
}

const PAGE_SIZE = 25
// A shipment more than this many times our estimate, or under its inverse, is far off.
const FAR_OFF_RATIO = 2

const VIEW_OPTIONS = [
  { value: 'manufacturer', label: 'Manufacturers' },
  { value: 'category', label: 'Categories' },
  { value: 'product', label: 'Products' },
] as const

const SEARCH_PLACEHOLDER: Record<View, string> = {
  manufacturer: 'Search manufacturers',
  category: 'Search categories',
  product: 'Search model, manufacturer or category',
}

const VOLUME_WORDS = ['bigger', 'smaller'] as const
const WEIGHT_WORDS = ['heavier', 'lighter'] as const

function isFarOff(ratio: number | null | undefined): boolean {
  return ratio != null && (ratio > FAR_OFF_RATIO || ratio < 1 / FAR_OFF_RATIO)
}

// "3.2× bigger" / "2.3× smaller" / "same": how what shipped compares with what we quoted.
function ratioPhrase(ratio: number, words: readonly [string, string]): string {
  const factor = ratio >= 1 ? ratio : 1 / ratio
  if (factor < 1.05) return 'same'
  return `${formatNumber(factor, 1)}× ${ratio >= 1 ? words[0] : words[1]}`
}

// Two decimals for small measures, so 0.09 ft³ does not read as 0.1.
function formatMeasure(value: number): string {
  return formatNumber(value, Math.abs(value) < 1 ? 2 : 1)
}

function formatAgo(value: string | null | undefined): string {
  if (!value) return 'never'
  const minutes = Math.max(0, Math.round((Date.now() - parseUTCDate(value).getTime()) / 60000))
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours} h ago`
  return `${Math.round(hours / 24)} days ago`
}

function formatShipDate(value: string): string {
  return parseUTCDate(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function useShipmentAccuracy(params: {
  view: View
  q: string
  farOffOnly: boolean
  sort: Sort
  page: number
  group?: GroupFilter
}) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: ['shipping-calculator', 'shipment-accuracy', params],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/shipping-calculator/shipment-accuracy', {
        params: {
          query: {
            view: params.view,
            q: params.q || undefined,
            far_off_only: params.farOffOnly,
            sort: params.sort,
            page: params.page,
            limit: PAGE_SIZE,
            manufacturer_id: params.group?.kind === 'manufacturer' ? params.group.id : undefined,
            category_id: params.group?.kind === 'category' ? params.group.id : undefined,
          },
        },
      })
      if (error) throw error
      return data!
    },
    placeholderData: keepPreviousData,
  })
}

export function ShipmentAccuracyTab() {
  const [view, setView] = useState<View>('manufacturer')
  const [q, setQ] = useState('')
  const [farOffOnly, setFarOffOnly] = useState(true)
  const [sort, setSort] = useState<Sort>('gap')
  const [page, setPage] = useState(1)
  const [group, setGroup] = useState<GroupFilter | undefined>()
  const accuracy = useShipmentAccuracy({ view, q, farOffOnly, sort, page, group })
  const data = accuracy.data

  function openGroup(kind: GroupFilter['kind'], row: Group) {
    if (row.id == null) return
    setGroup({ kind, id: row.id, name: row.name })
    setView('product')
    setQ('')
    setFarOffOnly(false)
    setPage(1)
  }

  return (
    <section aria-labelledby="shipment-accuracy-heading" className="space-y-4 pt-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id="shipment-accuracy-heading" className="text-base font-semibold">
          Shipped vs quoted
        </h2>
        {data && (
          <p className="text-sm tabular-nums text-muted-foreground">
            {formatNumber(data.products_far_off, 0)} of {formatNumber(data.products_compared, 0)}{' '}
            shipped products more than 2× off · Updated {formatAgo(data.last_recorded_at)}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <ReviewChoiceGroup
          label="Group by"
          value={view}
          options={VIEW_OPTIONS}
          onChange={(value) => {
            setView(value)
            setGroup(undefined)
            setQ('')
            setPage(1)
          }}
        />
        {group && (
          <Badge variant="outline" className="h-8 gap-1.5 rounded-md px-2.5 text-sm font-normal">
            {group.name}
            <button
              type="button"
              aria-label={`Show all products, not just ${group.name}`}
              className="rounded text-muted-foreground hover:text-foreground"
              onClick={() => {
                setGroup(undefined)
                setPage(1)
              }}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </Badge>
        )}
        <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <SearchInput
            type="search"
            value={q}
            onChange={(event) => {
              setQ(event.target.value)
              setPage(1)
            }}
            placeholder={SEARCH_PLACEHOLDER[view]}
            aria-label={SEARCH_PLACEHOLDER[view]}
            className="pl-8"
          />
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="shipment-far-off-only"
            checked={farOffOnly}
            onCheckedChange={(checked) => {
              setFarOffOnly(checked)
              setPage(1)
            }}
          />
          <Label htmlFor="shipment-far-off-only" className="text-sm font-normal">
            Only more than 2× off
          </Label>
        </div>
        <Select
          value={sort}
          onValueChange={(value) => {
            setSort(value as Sort)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-[160px] sm:ml-auto" aria-label="Sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="gap">Biggest gap</SelectItem>
            <SelectItem value="shipped">
              {view === 'product' ? 'Newest' : 'Most shipped'}
            </SelectItem>
            <SelectItem value="name">Name</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {accuracy.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      ) : accuracy.error || !data ? (
        <p className="rounded-lg border px-4 py-6 text-sm text-muted-foreground">
          Shipped vs quoted couldn’t load. Refresh the page to try again.
        </p>
      ) : data.products_compared === 0 ? (
        <p className="rounded-lg border px-4 py-6 text-sm text-muted-foreground">
          No booked shipments to compare yet.
        </p>
      ) : data.total === 0 ? (
        <p className="rounded-lg border px-4 py-6 text-sm text-muted-foreground">
          {farOffOnly && !q ? 'Nothing is more than 2× off.' : 'Nothing matches these filters.'}
        </p>
      ) : (
        <div className={cn(accuracy.isPlaceholderData && 'opacity-60 transition-opacity')}>
          {view === 'product' ? (
            <ProductTable products={data.products ?? []} hide={group?.kind} />
          ) : (
            <GroupTable
              kind={view}
              groups={data.groups ?? []}
              onOpen={(row) => openGroup(view, row)}
            />
          )}
          <Pager data={data} view={view} onPage={setPage} />
        </div>
      )}
    </section>
  )
}

function Gap({
  ratio,
  words,
  label,
}: {
  ratio: number | null | undefined
  words: readonly [string, string]
  label?: string
}) {
  if (ratio == null) return <span className="text-muted-foreground">—</span>
  const farOff = isFarOff(ratio)
  return (
    <span
      className={cn(
        'inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium tabular-nums',
        farOff ? 'bg-amber-100 text-amber-900' : 'bg-muted text-muted-foreground',
      )}
    >
      {label && `${label} `}
      {ratioPhrase(ratio, words)}
    </span>
  )
}

function GroupName({ group }: { group: Group }) {
  return group.manufacturer ? (
    <Link
      to="/manufacturers/$manuId"
      params={{ manuId: group.manufacturer.uuid }}
      className="font-medium text-primary hover:underline"
    >
      {group.name}
    </Link>
  ) : (
    <span className="font-medium">{group.name}</span>
  )
}

function ProductsButton({ group, onOpen }: { group: Group; onOpen: () => void }) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onOpen}
      disabled={group.id == null}
      aria-label={`Show ${group.products} shipped products for ${group.name}`}
      className="tabular-nums"
    >
      {formatNumber(group.products, 0)} {group.products === 1 ? 'product' : 'products'}
    </Button>
  )
}

function GroupTable({
  kind,
  groups,
  onOpen,
}: {
  kind: 'manufacturer' | 'category'
  groups: Group[]
  onOpen: (group: Group) => void
}) {
  return (
    <>
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{kind === 'manufacturer' ? 'Manufacturer' : 'Category'}</TableHead>
              <TableHead>Volume</TableHead>
              <TableHead>Weight</TableHead>
              <TableHead className="text-right">More than 2× off</TableHead>
              <TableHead className="text-right">Shipped</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={`${group.id}:${group.name}`}>
                <TableCell>
                  <GroupName group={group} />
                </TableCell>
                <TableCell>
                  <Gap ratio={group.volume_ratio} words={VOLUME_WORDS} />
                </TableCell>
                <TableCell>
                  <Gap ratio={group.weight_ratio} words={WEIGHT_WORDS} />
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatNumber(group.far_off_products, 0)}
                </TableCell>
                <TableCell className="text-right">
                  <ProductsButton group={group} onOpen={() => onOpen(group)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ul className="space-y-2 md:hidden">
        {groups.map((group) => (
          <li key={`${group.id}:${group.name}`} className="rounded-lg border p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <GroupName group={group} />
                <p className="text-xs tabular-nums text-muted-foreground">
                  {formatNumber(group.far_off_products, 0)} more than 2× off
                </p>
              </div>
              <ProductsButton group={group} onOpen={() => onOpen(group)} />
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Gap label="Volume" ratio={group.volume_ratio} words={VOLUME_WORDS} />
              <Gap label="Weight" ratio={group.weight_ratio} words={WEIGHT_WORDS} />
            </div>
          </li>
        ))}
      </ul>
    </>
  )
}

function Measure({
  shipped,
  quoted,
  ratio,
  unit,
  words,
}: {
  shipped: number | null | undefined
  quoted: number | null | undefined
  ratio: number | null | undefined
  unit: string
  words: readonly [string, string]
}) {
  if (shipped == null) return <span className="text-muted-foreground">—</span>
  return (
    <div className="tabular-nums">
      <span className={cn(isFarOff(ratio) && 'font-semibold text-amber-900 dark:text-amber-200')}>
        {formatMeasure(shipped)} {unit}
      </span>
      {quoted != null && (
        <p className="text-xs text-muted-foreground">
          quoted {formatMeasure(quoted)}
          {ratio != null && ` · ${ratioPhrase(ratio, words)}`}
        </p>
      )}
    </div>
  )
}

function ProductIdentity({ product, hide }: { product: Product; hide?: GroupFilter['kind'] }) {
  return (
    <div className="min-w-0">
      <Link
        to="/products/$productId"
        params={{ productId: product.product_uuid }}
        className="font-medium text-primary hover:underline"
      >
        {product.model ?? 'Product'}
      </Link>
      <p className="text-xs text-muted-foreground">
        {product.manufacturer && hide !== 'manufacturer' && (
          <Link
            to="/manufacturers/$manuId"
            params={{ manuId: product.manufacturer.uuid }}
            className="hover:underline"
          >
            {product.manufacturer.name}
          </Link>
        )}
        {product.manufacturer &&
          hide !== 'manufacturer' &&
          product.category &&
          hide !== 'category' &&
          ' · '}
        {hide !== 'category' && product.category}
      </p>
    </div>
  )
}

function ShipmentRef({ product }: { product: Product }) {
  return (
    <div>
      {product.po_number && product.purchase_order_uuid ? (
        <Link
          to="/purchase-orders/$poId"
          params={{ poId: product.purchase_order_uuid }}
          className="text-primary hover:underline"
        >
          {product.po_number}
        </Link>
      ) : (
        <span>{product.po_number ?? '—'}</span>
      )}
      <p className="text-xs text-muted-foreground">
        {product.shipment_uuid ? (
          <Link
            to="/shipments/$shipmentId"
            params={{ shipmentId: product.shipment_uuid }}
            className="hover:underline"
          >
            Booked {formatShipDate(product.booked_at)}
          </Link>
        ) : (
          <>Booked {formatShipDate(product.booked_at)}</>
        )}
      </p>
    </div>
  )
}

function ProductTable({ products, hide }: { products: Product[]; hide?: GroupFilter['kind'] }) {
  return (
    <>
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Shipment</TableHead>
              <TableHead className="text-right">Weight each</TableHead>
              <TableHead className="text-right">Volume each</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.product_id}>
                <TableCell className="max-w-[340px] whitespace-normal align-top">
                  <ProductIdentity product={product} hide={hide} />
                </TableCell>
                <TableCell className="align-top">
                  <ShipmentRef product={product} />
                </TableCell>
                <TableCell className="text-right align-top">
                  <Measure
                    shipped={product.weight_each}
                    quoted={product.estimated_weight}
                    ratio={product.weight_ratio}
                    unit="lb"
                    words={WEIGHT_WORDS}
                  />
                </TableCell>
                <TableCell className="text-right align-top">
                  <Measure
                    shipped={product.volume_cuft_each}
                    quoted={product.estimated_volume_cuft}
                    ratio={product.volume_ratio}
                    unit="ft³"
                    words={VOLUME_WORDS}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ul className="space-y-2 lg:hidden">
        {products.map((product) => (
          <li key={product.product_id} className="rounded-lg border p-3">
            <div className="flex items-start justify-between gap-3">
              <ProductIdentity product={product} hide={hide} />
              <div className="shrink-0 text-right text-sm">
                <ShipmentRef product={product} />
              </div>
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">Weight each</dt>
                <dd>
                  <Measure
                    shipped={product.weight_each}
                    quoted={product.estimated_weight}
                    ratio={product.weight_ratio}
                    unit="lb"
                    words={WEIGHT_WORDS}
                  />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Volume each</dt>
                <dd>
                  <Measure
                    shipped={product.volume_cuft_each}
                    quoted={product.estimated_volume_cuft}
                    ratio={product.volume_ratio}
                    unit="ft³"
                    words={VOLUME_WORDS}
                  />
                </dd>
              </div>
            </dl>
          </li>
        ))}
      </ul>
    </>
  )
}

function Pager({
  data,
  view,
  onPage,
}: {
  data: Accuracy
  view: View
  onPage: (page: number) => void
}) {
  const noun =
    view === 'manufacturer' ? 'manufacturers' : view === 'category' ? 'categories' : 'products'
  return (
    <div className="flex items-center justify-between py-3">
      <p className="text-sm tabular-nums text-muted-foreground">
        Page {data.page} of {Math.max(data.pages, 1)} ({formatNumber(data.total, 0)} {noun})
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPage(Math.max(1, data.page - 1))}
          disabled={data.page <= 1}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPage(data.page + 1)}
          disabled={data.page >= data.pages}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
